import {useState, useCallback, useEffect} from 'react';
import {TabType} from '../types';
import {useSelector} from 'react-redux';
import {TState} from '../../../redux/store.type';
import {getEventSettings} from '../../../redux/event/eventActions';
import setGlobalState from '../../../redux/setGlobalState';
import {validateWhatsAppText} from '../../../pages/branding/whatsAppScreen/whatsAppValidation';
import {useTranslation} from 'react-i18next';

interface UseSendingLogicProps {
    whatsappProps: any;
    smsProps: any;
    emailProps: any;
    amount: number;
    onHasEnoughCredits?: () => void;
}

export const useSendingLogic = ({whatsappProps, smsProps, emailProps, amount, onHasEnoughCredits}: UseSendingLogicProps) => {
    const {t} = useTranslation('branding');
    const whatsappAllowed = useSelector((s: TState) => s.account.permissions?.find(p => p.permission === 'whatsApp')?.status === 1);
    const getEventSettingsXHR = useSelector((state: TState) => state.event.getEventSettingsXHR);
    const account = useSelector((state: TState) => state.account);
    const [currentTab, setCurrentTab] = useState<TabType>(null);
    const [sendingTab, setSendingTab] = useState<TabType>(null);
    
    const props = {
        whatsapp: whatsappProps,
        sms: smsProps,
        email: emailProps
    };
    const sendingXHR = props[currentTab]?.sendingXHR;

    const validateWhatsAppContent = () => {
        if(currentTab === 'whatsapp') {
            const text = whatsappProps?.text || '';
            return validateWhatsAppText(text, t);
        }
        return true;
    };

    const handleTabChange = (tab: TabType) => {
        setCurrentTab(tab);
    };
    const checkIfHasEnoughCredits = () => {
        if(currentTab === 'whatsapp') {
            return true;
        }
        if(currentTab === 'sms') {
            const smsCredits = account.billPackage?.smsFreeAmount || 0;
            return smsCredits >= amount;
        } else if(currentTab === 'email') {
            const emailCredits = account.billPackage?.emailFreeAmount || 0;
            return emailCredits >= amount;
        }
        return true;
    };

    const handleSend = async () => {
        try {
            if(!validateWhatsAppContent()) {
                return false;
            }

            const hasEnoughCredits = checkIfHasEnoughCredits();
            if(!hasEnoughCredits) {
                setGlobalState('ui.show.isOpenUpgradeModal', true);
            } else {
                if(onHasEnoughCredits) {
                    onHasEnoughCredits();
                } else {
                    const result = await props[currentTab].send();
                    if(result) {
                        setSendingTab(currentTab);
                        setCurrentTab('success');
                    }
                }
            }
        } catch (error) {
            console.error(`Error sending ${currentTab} message:`, error);
            return false;
        }
    };

    const handleActualSend = async () => {
        try {
            if(!validateWhatsAppContent()) {
                return false;
            }

            const result = await props[currentTab].send();
            if(result) {
                setSendingTab(currentTab);
                setGlobalState('ui.show.isOpenUpgradeModal', false);
                setCurrentTab('success');
            }
            return result;
        } catch (error) {
            console.error(`Error sending ${currentTab} message:`, error);
            return false;
        }
    };

    const handleTrialSend = useCallback(async (contacts: string[]) => {
        try {
            if(!validateWhatsAppContent()) {
                return false;
            }

            const hasEnoughCredits = checkIfHasEnoughCredits();
            if(!hasEnoughCredits) {
                setGlobalState('ui.show.isOpenUpgradeModal', true);
            } else {
                const result = await props[currentTab].sendTrial(contacts);   
                if(result) {
                    setSendingTab(currentTab);
                }
                return result;
            }
        } catch (err) {
            console.error(`Error sending trial ${currentTab}:`, err);
            return false;
        }
    }, [currentTab, props]);

    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then();
        }
        if(getEventSettingsXHR==='SUCCESS') {
            setCurrentTab(whatsappAllowed ? 'whatsapp' : 'email');
        }
    }, [getEventSettingsXHR]);

    return {
        currentTab,
        sendingTab,
        handleTabChange,
        handleSend,
        handleActualSend,
        handleTrialSend,
        whatsappAllowed,
        sendingXHR
    };
};
